import { useEffect, useRef, useState } from "react";
import { BarChart, LogIn, Mail, Menu, Search, X, Upload, User } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ebidImage from "../../assets/images/logo.svg";
import { useTheme } from "../../theme/ThemeProvider";
import authService from '../../services/authService';
import { ROUTES } from '../../routes/routePaths';
import { useAuth } from '../../context/AuthContext';

function Header() {
  const [isOpen, setIsOpen] = useState(false);

  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const menuRef = useRef(null);
  const { isAuthenticated } = useAuth();

  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate('/');
  };

  const handleLogout = async () => {
    await authService.logout();
  };

  const handleNavClick = () => {
    setIsOpen(false);
  };

  const isLoggedIn = isAuthenticated;

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/95">
      <div className="container flex h-16 items-center px-4">
        <div className="flex items-center space-x-2">
          <Link to="/" onClick={handleLogoClick} className="flex items-center space-x-2">
            <img src={ebidImage} alt="Logo" className="h-10 object-contain" />
          </Link>
        </div>

        <button
          className="ml-auto md:hidden z-50"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>

        <nav
          ref={menuRef}
          className={`${isOpen
            ? "absolute top-16 left-0 w-full bg-gradient-to-b from-[rgb(239,246,255)] to-white dark:from-gray-800 dark:to-gray-900 shadow-lg z-40"
            : "hidden"
            } md:static md:flex md:ml-auto md:mr-3 md:border-0 md:bg-transparent md:shadow-none`}
        >
          <ul className="flex flex-col md:flex-row space-y-1 md:space-y-0 md:space-x-3 p-3 md:p-0 list-none">
            {/* Advanced Search */}
            <li>
              <Link
                to={ROUTES.ADVANCED_SEARCH}
                onClick={handleNavClick}
                className="py-1.5 text-base hover:text-primary text-blue-400 font-semibold flex items-center space-x-1.5"
              >
                <Search className="w-4 h-4" />
                <span>Advanced Search</span>
              </Link>
            </li>

            <li>
              <Link
                to={ROUTES.CONTRACTS}
                onClick={() => setIsOpen(false)}
                className="py-1.5 text-base hover:text-primary text-blue-400 font-semibold flex items-center space-x-1.5"
              >
                <span>Contract Awards</span>
              </Link>
            </li>

            {/* Buttons as list items to keep layout consistent */}
            <li className="flex items-center space-x-1.5 flex-wrap gap-y-1">
              <button
                onClick={() => navigate(ROUTES.REQUEST_DEMO)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold
                  text-blue-600 border border-blue-500 rounded-full
                  hover:bg-blue-500 hover:text-white transition-all duration-300"
              >
                <span>🗓️</span>
                Request a Demo
              </button>

              <button
                onClick={() => navigate(ROUTES.PUBLISH_TENDER)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold
                  text-green-700 border border-green-500 rounded-full
                  hover:bg-green-500 hover:text-white transition-all duration-300"
              >
                <Upload className="w-4 h-4" />
                Publish Tender
              </button>

              {isAuthenticated ? (
                <Link
                  to={ROUTES.DASHBOARD}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    location.pathname === '/dashboard'
                      ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-blue-400 dark:hover:bg-gray-700'
                  }`}
                >
                  <BarChart className="w-4 h-4" />
                  <span>Dashboard</span>
                </Link>
              ) : (
                <>
                  <button
                    onClick={() => navigate(ROUTES.LOGIN)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold
                      hover:bg-gradient-to-br from-blue-500 to-blue-700 hover:text-white
                      rounded-full border-2 border-blue-500 text-blue-500
                      transition-all duration-300 shadow-sm hover:shadow-lg"
                  >
                    <LogIn className="w-4 h-4" />
                    Login
                  </button>

                  <button
                    onClick={() => navigate(ROUTES.REGISTER)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold
                      hover:bg-gradient-to-br from-purple-600 to-indigo-600 hover:text-white
                      rounded-full border-2 border-purple-600 text-purple-600
                      transition-all duration-300 shadow-sm hover:shadow-lg"
                  >
                    <User className="w-4 h-4" />
                    Register
                  </button>
                </>
              )}

              <button
                onClick={() => navigate(ROUTES.SUBSCRIBE)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold
                  text-white bg-blue-600 rounded-full
                  hover:bg-blue-700 transition shadow-sm"
              >
                <Mail className="w-4 h-4" />
                Subscribe
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
}

export default Header;